import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import { MOBILE_ORDER_DISPLAY_ONLY_EDITED } from 'features/Store/constants';
import { COLUMN_WIDTH } from 'features/Store/pages/StoreSettingDetailPage';
import {
  selectStoreSettingDetailDisplayTimingSource,
  selectStoreSettingDetailDisplayType,
  selectStoreSettingDetailExpandAll,
  selectStoreSettingEditedMenuBookIds,
  storeSettingDetailActions,
} from 'features/Store/storeSettingDetailSlice';
import {
  countMenuHasEditedMObileOrderDisplayFlg,
  countMultiplePosCodeRegisteredOnMenu,
  getDefaultMobileOrderDisplayFlg,
  getIsExpandStatus,
  hasEditedMobileOrderDisplayFlg,
} from 'features/Store/utils';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { MINUS_TEXT, PLUS_TEXT } from 'utils';
import { Course, Genre, Menu, MenuBook, MenuDetail, SubGenre } from '../../models';
import MobileOrderDisplayModal from '../MobileOrderDisplayModal';

export interface MenuRowCollapseProps {
  menuBook: MenuBook;
  course?: Course;
  genre: Genre;
  subgenre: SubGenre;
  menu: Menu;
  children?: React.ReactNode;
}

export interface MobileOrderDisplayModalData {
  posCodeId: number;
  posCode: string;
  restaurantId: number;
  menuName: string;
  menuCode: string;
  couponClassification: number;
  defaultMobileOrderDisplayFlg: boolean;
}

function MenuRow({ menuBook, course, genre, subgenre, menu, children }: MenuRowCollapseProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const editedMenuBookIds = useAppSelector(selectStoreSettingEditedMenuBookIds);
  const isExpand = useAppSelector(selectStoreSettingDetailExpandAll);
  const displayType = useAppSelector(selectStoreSettingDetailDisplayType);
  const displayTimingSource = useAppSelector(selectStoreSettingDetailDisplayTimingSource);

  const [open, setOpen] = useState(getIsExpandStatus(menuBook, editedMenuBookIds, isExpand));

  const [showMobileOrderDisplayModal, setShowMobileOrderDisplayModal] = useState(false);
  const [mobileOrderDisplayModalData, setMobileOrderDisplayModalData] = useState<MobileOrderDisplayModalData>();

  let pathId = `${menuBook.id}-${genre.id}-${subgenre.id}-${menu.id}`;
  if (course) {
    pathId = `${menuBook.id}-${course.id}-${genre.id}-${subgenre.id}-${menu.id}`;
  }

  useEffect(() => {
    const btnExpandElem = document.querySelector(`#menu-${pathId}-row .dan-btn-expand`) as HTMLButtonElement;
    function handleBtnExpandElemClick(event: MouseEvent) {
      event.stopPropagation();
      setOpen(!open);
      dispatch(storeSettingDetailActions.setExpandAll(undefined));
      btnExpandElem.innerHTML = !open ? MINUS_TEXT : PLUS_TEXT;
      btnExpandElem?.setAttribute('aria-expanded', !open ? 'true' : 'false');
    }

    if (btnExpandElem) {
      btnExpandElem.addEventListener('click', handleBtnExpandElemClick);
    }

    return () => {
      if (btnExpandElem) {
        btnExpandElem.removeEventListener('click', handleBtnExpandElemClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, displayType]);

  useEffect(() => {
    if (isExpand !== undefined) {
      setOpen(getIsExpandStatus(menuBook, editedMenuBookIds, isExpand));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpand]);

  const assingedMenuDetailList = menu.menu_details.filter(
    (menuDetail) => menuDetail.counter_pattern_display_flg !== null
  );

  const hasEdited = hasEditedMobileOrderDisplayFlg(assingedMenuDetailList[0]);
  const defaultMobileOrderDisplayFlg = getDefaultMobileOrderDisplayFlg(assingedMenuDetailList[0]);
  const numberofMulitpleRegisteredPosCode = countMultiplePosCodeRegisteredOnMenu(menu);
  const isErrorMulitpleRegisteredPosCode = numberofMulitpleRegisteredPosCode > 1;

  function handleCloseMobileOrderDisplayModal(isDataChanged: boolean = false) {
    if (isDataChanged) {
      dispatch(storeSettingDetailActions.reloadData());
    }

    setShowMobileOrderDisplayModal(false);
    setMobileOrderDisplayModalData(undefined);
  }

  function handleOpenMobileOrderDisplayModal(menuDetail?: MenuDetail) {
    if (menuDetail) {
      const currentDefaultMobileOrderDisplayFlg = getDefaultMobileOrderDisplayFlg(menuDetail);
      setShowMobileOrderDisplayModal(true);
      setMobileOrderDisplayModalData({
        posCodeId: menuDetail.pos_code_id,
        posCode: menuDetail.pos_code_cd,
        restaurantId: Number(restaurantId) || 0,
        menuName: menu.name,
        menuCode: menu.cd,
        couponClassification: menu.coupon_classification,
        defaultMobileOrderDisplayFlg: currentDefaultMobileOrderDisplayFlg || false,
      });
    }
  }

  const countTotalEditedMobileOrderDisplay = countMenuHasEditedMObileOrderDisplayFlg(menu);
  if (displayType === MOBILE_ORDER_DISPLAY_ONLY_EDITED && countTotalEditedMobileOrderDisplay === 0) {
    return null;
  }

  return (
    <>
      <div
        id={`menu-${pathId}-row`}
        className={`c-menu-row dan-tr content-visibility-auto ${hasEdited ? 'has-edited' : ''}`}
      >
        <div
          className="dan-td"
          style={{
            width: `${COLUMN_WIDTH.slice(0, 4).reduce((sum, width) => sum + width, 0)}%`,
          }}
        ></div>
        <div className="dan-td" style={{ width: `${COLUMN_WIDTH[4]}%` }}>
          <button
            className="dan-btn-expand"
            tabIndex={-1}
            aria-controls={`menu-${pathId}-content`}
            aria-expanded={false}
          >
            {open ? MINUS_TEXT : PLUS_TEXT}
          </button>
          <span className={`${isErrorMulitpleRegisteredPosCode ? 'text-danger' : ''}`}>
            {menu.name} ({displayTimingSource?.[menu.display_timing]})
          </span>
        </div>
        <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[5]}%` }}>
          {numberofMulitpleRegisteredPosCode === 0 ? (
            <span className="not-apply">{t('StoreSettingDetailPage.not_apply')}</span>
          ) : (
            <span className={`${isErrorMulitpleRegisteredPosCode ? 'text-danger' : ''}`}>
              {assingedMenuDetailList[0] && Number(assingedMenuDetailList[0].pos_code_cd)}
            </span>
          )}
        </div>
        <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[6]}%` }}>
          {defaultMobileOrderDisplayFlg === undefined ? null : (
            <Button
              className={clsx('btn-edit-mobile-display-flg', {
                'btn--dark-green': defaultMobileOrderDisplayFlg,
                'btn--green': !defaultMobileOrderDisplayFlg,
              })}
              onClick={handleOpenMobileOrderDisplayModal.bind(null, assingedMenuDetailList[0])}
            >
              {defaultMobileOrderDisplayFlg ? t('StoreSettingDetailPage.enable') : t('StoreSettingDetailPage.disable')}
            </Button>
          )}
        </div>
        <div
          className="dan-td"
          style={{
            width: `${COLUMN_WIDTH.slice(7, 17).reduce((sum, width) => sum + width, 0)}%`,
          }}
        ></div>
      </div>
      {assingedMenuDetailList.map(function (menuDetail, index) {
        if (index === 0) {
          return null;
        }

        const hasEdited = hasEditedMobileOrderDisplayFlg(menuDetail);
        const otherDefaultMobileOrderDisplayFlg = getDefaultMobileOrderDisplayFlg(menuDetail);

        if (displayType === MOBILE_ORDER_DISPLAY_ONLY_EDITED && !hasEdited) {
          return null;
        }

        return (
          <div
            key={index}
            id={`menu-${pathId}-detail-row-${index}`}
            className={`c-menu-row dan-tr ${hasEdited ? 'has-edited' : ''}`}
          >
            <div
              className="dan-td"
              style={{
                width: `${COLUMN_WIDTH.slice(0, 4).reduce((sum, width) => sum + width, 0)}%`,
              }}
            ></div>
            <div className="dan-td" style={{ width: `${COLUMN_WIDTH[4]}%` }}></div>
            <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[5]}%` }}>
              {menuDetail.counter_pattern_display_flg === null ? (
                <span className="not-apply">{t('StoreSettingDetailPage.not_apply')}</span>
              ) : (
                <span className={`${isErrorMulitpleRegisteredPosCode ? 'text-danger' : ''}`}>
                  {Number(menuDetail.pos_code_cd)}
                </span>
              )}
            </div>
            <div className="dan-td justify-content-center" style={{ width: `${COLUMN_WIDTH[6]}%` }}>
              {otherDefaultMobileOrderDisplayFlg === undefined ? null : (
                <Button
                  className={clsx('btn-edit-mobile-display-flg', {
                    'btn--dark-green': otherDefaultMobileOrderDisplayFlg,
                    'btn--green': !otherDefaultMobileOrderDisplayFlg,
                  })}
                  onClick={handleOpenMobileOrderDisplayModal.bind(null, menuDetail)}
                >
                  {otherDefaultMobileOrderDisplayFlg
                    ? t('StoreSettingDetailPage.enable')
                    : t('StoreSettingDetailPage.disable')}
                </Button>
              )}
            </div>
            <div
              className="dan-td"
              style={{
                width: `${COLUMN_WIDTH.slice(7, 17).reduce((sum, width) => sum + width, 0)}%`,
              }}
            ></div>
          </div>
        );
      })}
      <div style={{ contentVisibility: open ? 'auto' : 'hidden' }}>
        <div id={`menu-${pathId}-content`}>{children}</div>
      </div>

      {showMobileOrderDisplayModal &&
        mobileOrderDisplayModalData &&
        createPortal(
          <MobileOrderDisplayModal
            posCodeId={mobileOrderDisplayModalData.posCodeId}
            posCode={mobileOrderDisplayModalData.posCode}
            restaurantId={mobileOrderDisplayModalData.restaurantId}
            menuName={mobileOrderDisplayModalData.menuName}
            menuCode={mobileOrderDisplayModalData.menuCode}
            couponClassification={mobileOrderDisplayModalData.couponClassification}
            defaultMobileOrderDisplayFlg={mobileOrderDisplayModalData.defaultMobileOrderDisplayFlg}
            onClose={handleCloseMobileOrderDisplayModal}
          />,
          document.body
        )}
    </>
  );
}

export default MenuRow;
